import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight, ChevronDown, Camera, Megaphone, Monitor, Compass } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import { serviceMenu } from "../i18n/services";
import LanguageSwitcher from "./LanguageSwitcher";
import SiteLogo from "./SiteLogo";

const iconMap = {
  "social-media-management": Camera,
  "ads": Megaphone,
  "website": Monitor,
  "consultanta": Compass,
};

export default function IslandHeader() {
  const { t, lang } = useLanguage();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const location = useLocation();
  const services = serviceMenu[lang] || serviceMenu.ro;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    setServicesOpen(false);
    setMobileServicesOpen(false);
  }, [location.pathname]);

  const isServicesActive = location.pathname.startsWith("/services");

  const navLinkClass = ({ isActive }) =>
    `relative px-4 py-2 text-[13px] font-medium tracking-tight transition-colors rounded-full ${
      isActive ? "text-[#3e2723]" : "text-[#3e2723]/60 hover:text-[#3e2723]"
    }`;

  const renderPill = (isActive) =>
    isActive && (
      <motion.span
        layoutId="nav-pill"
        className="absolute inset-0 rounded-full bg-[#fbd9e5]"
        transition={{ type: "spring", stiffness: 350, damping: 30 }}
      />
    );

  return (
    <>
      <motion.header
        initial={{ y: -40, opacity: 0, x: "-50%" }}
        animate={{ y: 0, opacity: 1, x: "-50%" }}
        transition={{ duration: 0.8, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
        className="fixed top-4 sm:top-6 left-1/2 z-[100] w-[calc(100%-1.5rem)] max-w-6xl"
        data-testid="island-header"
      >
        <div
          className={`relative flex items-center justify-between gap-2 rounded-full px-3 sm:px-4 py-2 sm:py-2.5 transition-all duration-500 ${
            scrolled
              ? "bg-white/75 backdrop-blur-2xl backdrop-saturate-150 border border-[#3e2723]/10 shadow-[0_10px_40px_rgba(62,39,35,0.08)]"
              : "bg-white/55 backdrop-blur-xl backdrop-saturate-150 border border-white/50 shadow-[0_8px_30px_rgba(62,39,35,0.06)]"
          }`}
        >
          {/* LEFT: Navigation (desktop) */}
          <nav className="hidden md:flex items-center gap-1">
            <NavLink to="/" end className={navLinkClass} data-testid="nav-home">
              {({ isActive }) => (
                <>
                  {renderPill(isActive)}
                  <span className="relative">{t.nav.home}</span>
                </>
              )}
            </NavLink>

            {/* Services trigger — dropdown wrapper */}
            <div
              className="relative"
              onMouseEnter={() => setServicesOpen(true)}
              onMouseLeave={() => setServicesOpen(false)}
            >
              <NavLink
                to="/services"
                end
                className={() =>
                  `relative inline-flex items-center gap-1 px-4 py-2 text-[13px] font-medium tracking-tight transition-colors rounded-full ${
                    isServicesActive ? "text-[#3e2723]" : "text-[#3e2723]/60 hover:text-[#3e2723]"
                  }`
                }
                data-testid="nav-services"
              >
                {renderPill(isServicesActive)}
                <span className="relative">{t.nav.services}</span>
                <ChevronDown
                  className={`relative h-3 w-3 transition-transform duration-300 ${
                    servicesOpen ? "rotate-180" : ""
                  }`}
                />
              </NavLink>

              {/* Dropdown: wrap AnimatePresence content in static positioning div so framer-motion doesn't override translate-x-1/2 */}
              <div className="absolute left-1/2 top-full pt-4 -translate-x-1/2 w-[420px]">
                <AnimatePresence>
                  {servicesOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: 8, scale: 0.98 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: 8, scale: 0.98 }}
                      transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
                      className="rounded-[1.5rem] bg-white/95 backdrop-blur-2xl border border-[#3e2723]/10 p-2 shadow-[0_20px_60px_rgba(62,39,35,0.15)]"
                      data-testid="services-dropdown"
                    >
                      {services.map((s) => {
                        const Icon = iconMap[s.slug] || Camera;
                        return (
                          <Link
                            key={s.slug}
                            to={`/services/${s.slug}`}
                            className="group flex items-center gap-4 rounded-[1.25rem] p-3 hover:bg-[#fbd9e5]/60 transition-colors duration-200"
                            data-testid={`dropdown-${s.slug}`}
                          >
                            <span className="inline-flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-[#fbd9e5] text-[#3e2723] group-hover:bg-[#3e2723] group-hover:text-[#fbd9e5] transition-colors">
                              <Icon className="h-4 w-4" />
                            </span>
                            <div className="flex-1 min-w-0">
                              <p className="text-[14px] font-semibold text-[#3e2723] leading-tight">
                                {s.label}
                              </p>
                              <p className="mt-0.5 text-[12px] text-[#7a5c55] leading-snug">
                                {s.desc}
                              </p>
                            </div>
                            <ArrowUpRight className="h-3.5 w-3.5 text-[#3e2723]/40 group-hover:text-[#3e2723] group-hover:rotate-45 transition-all" />
                          </Link>
                        );
                      })}
                      <Link
                        to="/services"
                        className="mt-1 flex items-center justify-between rounded-[1.25rem] bg-[#3e2723] text-[#fbd9e5] px-4 py-3 text-[13px] font-semibold hover:bg-[#2a1a17] transition-colors"
                        data-testid="dropdown-all"
                      >
                        <span>{lang === "ro" ? "Vezi toate serviciile" : "View all services"}</span>
                        <ArrowUpRight className="h-4 w-4" />
                      </Link>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>

            <NavLink to="/portfolio" className={navLinkClass} data-testid="nav-portfolio">
              {({ isActive }) => (
                <>
                  {renderPill(isActive)}
                  <span className="relative">{t.nav.portfolio}</span>
                </>
              )}
            </NavLink>
            <NavLink to="/about" className={navLinkClass} data-testid="nav-about">
              {({ isActive }) => (
                <>
                  {renderPill(isActive)}
                  <span className="relative">{t.nav.about}</span>
                </>
              )}
            </NavLink>
            <NavLink to="/contact" className={navLinkClass} data-testid="nav-contact">
              {({ isActive }) => (
                <>
                  {renderPill(isActive)}
                  <span className="relative">{t.nav.contact}</span>
                </>
              )}
            </NavLink>
          </nav>

          {/* CENTER: Brand logo (absolute centered on desktop, inline on mobile) */}
          <Link
            to="/"
            className="md:absolute md:left-1/2 md:-translate-x-1/2 flex items-center justify-center pl-1 md:pl-0 pr-1 z-10 shrink-0 min-w-0"
            data-testid="brand-logo"
          >
            <SiteLogo variant="header" />
          </Link>

          {/* RIGHT: Lang + CTA */}
          <div className="flex items-center gap-2">
            <LanguageSwitcher />
            <Link
              to="/contact"
              className="hidden sm:inline-flex items-center gap-1.5 rounded-full bg-[#3e2723] px-4 py-2 text-[13px] font-semibold text-[#fbd9e5] hover:bg-[#2a1a17] transition-all duration-300 shadow-sm hover:shadow-md hover:-translate-y-0.5"
              data-testid="nav-cta"
            >
              {t.nav.cta}
              <ArrowUpRight className="h-3.5 w-3.5" />
            </Link>

            <button
              type="button"
              onClick={() => setOpen((v) => !v)}
              className="md:hidden inline-flex h-9 w-9 items-center justify-center rounded-full bg-[#3e2723] text-[#fbd9e5]"
              aria-label="Toggle menu"
              data-testid="mobile-menu-toggle"
            >
              {open ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
            </button>
          </div>
        </div>
      </motion.header>

      {/* Mobile menu */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[90] md:hidden"
            data-testid="mobile-menu"
          >
            <div className="absolute inset-0 bg-[#3e2723]/40 backdrop-blur-md" onClick={() => setOpen(false)} />
            <motion.div
              initial={{ y: -20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: -20, opacity: 0 }}
              transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
              className="absolute top-24 left-4 right-4 rounded-[2rem] bg-white/95 backdrop-blur-xl border border-[#3e2723]/10 p-4 shadow-2xl max-h-[80vh] overflow-y-auto"
            >
              <nav className="flex flex-col gap-1">
                <Link to="/" className="flex items-center justify-between px-4 py-3 rounded-2xl text-[16px] font-medium text-[#3e2723]/80 hover:bg-[#fbd9e5]/50" data-testid="mobile-nav-home">
                  <span>{t.nav.home}</span>
                  <ArrowUpRight className="h-4 w-4 opacity-60" />
                </Link>

                {/* Services collapsible */}
                <div>
                  <button
                    type="button"
                    onClick={() => setMobileServicesOpen((v) => !v)}
                    className="flex w-full items-center justify-between px-4 py-3 rounded-2xl text-[16px] font-medium text-[#3e2723]/80 hover:bg-[#fbd9e5]/50"
                    data-testid="mobile-nav-services-toggle"
                    aria-expanded={mobileServicesOpen}
                  >
                    <span>{t.nav.services}</span>
                    <ChevronDown className={`h-4 w-4 transition-transform duration-300 ${mobileServicesOpen ? "rotate-180" : ""}`} />
                  </button>
                  <AnimatePresence initial={false}>
                    {mobileServicesOpen && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
                        className="overflow-hidden"
                        data-testid="mobile-services-submenu"
                      >
                        <div className="mt-1 mb-2 px-2 space-y-1">
                          <Link
                            to="/services"
                            className="flex items-center justify-between rounded-xl bg-[#3e2723] text-[#fbd9e5] px-3 py-2.5 text-[13px] font-semibold"
                            data-testid="mobile-nav-services"
                          >
                            <span>{lang === "ro" ? "Toate serviciile" : "All services"}</span>
                            <ArrowUpRight className="h-3.5 w-3.5" />
                          </Link>
                          {services.map((s) => {
                            const Icon = iconMap[s.slug] || Camera;
                            return (
                              <Link
                                key={s.slug}
                                to={`/services/${s.slug}`}
                                className="flex items-center gap-3 rounded-xl bg-[#fbd9e5]/40 p-2.5 text-[13px] font-medium text-[#3e2723]"
                                data-testid={`mobile-dropdown-${s.slug}`}
                              >
                                <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-[#3e2723] text-[#fbd9e5]">
                                  <Icon className="h-3.5 w-3.5" />
                                </span>
                                <span className="flex-1">{s.label}</span>
                              </Link>
                            );
                          })}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                <Link to="/portfolio" className="flex items-center justify-between px-4 py-3 rounded-2xl text-[16px] font-medium text-[#3e2723]/80 hover:bg-[#fbd9e5]/50" data-testid="mobile-nav-portfolio">
                  <span>{t.nav.portfolio}</span>
                  <ArrowUpRight className="h-4 w-4 opacity-60" />
                </Link>
                <Link to="/about" className="flex items-center justify-between px-4 py-3 rounded-2xl text-[16px] font-medium text-[#3e2723]/80 hover:bg-[#fbd9e5]/50" data-testid="mobile-nav-about">
                  <span>{t.nav.about}</span>
                  <ArrowUpRight className="h-4 w-4 opacity-60" />
                </Link>
                <Link to="/contact" className="flex items-center justify-between px-4 py-3 rounded-2xl text-[16px] font-medium text-[#3e2723]/80 hover:bg-[#fbd9e5]/50" data-testid="mobile-nav-contact">
                  <span>{t.nav.contact}</span>
                  <ArrowUpRight className="h-4 w-4 opacity-60" />
                </Link>
              </nav>
              <Link
                to="/contact"
                className="mt-4 inline-flex w-full items-center justify-center gap-1.5 rounded-full bg-[#3e2723] px-5 py-3.5 text-[14px] font-semibold text-[#fbd9e5]"
                data-testid="mobile-nav-cta"
              >
                {t.nav.cta}
                <ArrowUpRight className="h-4 w-4" />
              </Link>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
